import React, { type ReactNode } from 'react';
import { cn } from '../../../utils';
import type { Position } from '../../../types';
import { ShrinkIcon } from '../../shared/icons';

export interface DefaultWidgetPanelProps {
  /** Whether panel is open */
  isOpen: boolean;
  /** Close/Minimize handler */
  onClose: () => void;
  /** Position of the panel */
  position?: Position;
  /** Children content */
  children: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * Default style widget panel - ElevenLabs-inspired rounded design
 * with smooth popover animation
 */
export function DefaultWidgetPanel({
  isOpen,
  onClose,
  position = 'bottom-right',
  children,
  className,
}: DefaultWidgetPanelProps) {
  const [isMounted, setIsMounted] = React.useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimatingOut(false);
      setIsMounted(true);
    } else if (isMounted) {
      setIsAnimatingOut(true);
      const timer = setTimeout(() => {
        setIsMounted(false);
        setIsAnimatingOut(false);
      }, 280);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isMounted) return null;

  const panelWidth = 380;

  const positionStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: 96,
    ...(position === 'bottom-left' ? { left: 24 } : { right: 24 }),
    width: Math.min(panelWidth, typeof window !== 'undefined' ? window.innerWidth - 48 : panelWidth),
    height: typeof window !== 'undefined' ? Math.min(window.innerHeight - 130, 620) : 520,
    zIndex: 'var(--qcall-z-panel)',
    borderRadius: 16,
    transformOrigin: position === 'bottom-left' ? 'bottom left' : 'bottom right',
  };

  return (
    <>
      {/* Backdrop overlay with fade */}
      {isOpen && !isAnimatingOut && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            animation: 'qcall-overlay-fade-in 200ms ease forwards',
          }}
          onClick={onClose}
        />
      )}
      {isAnimatingOut && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            animation: 'qcall-overlay-fade-out 250ms ease forwards',
          }}
          onClick={onClose}
        />
      )}

      <div
        className={cn(
          'flex flex-col',
          'bg-[#F8F9FA]',
          'border border-[#e5e7eb]',
          'overflow-hidden',
          className
        )}
        style={{
          ...positionStyles,
          animation: isAnimatingOut
            ? 'qcall-popover-close 280ms cubic-bezier(0.4, 0, 1, 1) forwards'
            : 'qcall-popover-open 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.04)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Voice call panel"
      >
        {/* Close button - rounded 8px */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={onClose}
            className={cn(
              "flex items-center justify-center w-7 h-7 rounded-lg",
              "transition-all duration-150",
              "hover:scale-110 active:scale-90"
            )}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              color: '#313130',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.10)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(0, 0, 0, 0.06)';
            }}
            aria-label="Minimize panel"
          >
            <ShrinkIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative">
          {children}
        </div>

        {/* Footer */}
        <p className="text-slate-500 text-[9px] uppercase tracking-[0.2em] font-bold text-center py-2">Powered by QCALL AI</p>
      </div>
    </>
  );
}
